import React, { useRef, useState, useEffect } from "react";
import { launchGame, assignmentRequiresAuth } from "../../services/gameLaunchService";
import RecordingManager from "../recording/RecordingManager";
import "./GameLauncher.scss";

/**
 * Game Launcher Component
 * Launches web games in iframe with automatic recording
 */
function GameLauncher({ assignment, onClose, onError, onRequestExternal }) {
  const iframeRef = useRef(null);
  const recordingManagerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [gameUrl, setGameUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("idle"); // idle, starting, recording, stopping, stopped
  const [playTime, setPlayTime] = useState(0); // Playtime in seconds
  const [startTime, setStartTime] = useState(null);
  const chimeIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  
  // Permission states
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [permissionsStatus, setPermissionsStatus] = useState({
    camera: { granted: false, checking: false, error: null },
    microphone: { granted: false, checking: false, error: null },
    screenCapture: { granted: false, checking: false, error: null }
  });
  const [gameReady, setGameReady] = useState(false); // Game can be launched after permissions
  const screenCaptureStreamRef = useRef(null); // Store screen capture stream to reuse

  // Check permissions first, before launching game
  useEffect(() => {
    if (assignment && !permissionsChecked) {
      checkAllPermissions();
    }
  }, [assignment, permissionsChecked]);

  // Launch game only after permissions are granted and user clicks Continue
  useEffect(() => {
    if (gameReady && assignment && !gameUrl) {
      initializeGame();
    }
  }, [gameReady, assignment, gameUrl]);

  // Check all required permissions
  const checkAllPermissions = async () => {
    setPermissionsChecked(true);
    setLoading(true);
    
    // Check camera permission
    setPermissionsStatus(prev => ({
      ...prev,
      camera: { ...prev.camera, checking: true }
    }));
    
    try {
      const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the stream immediately - we just needed to check permission
      cameraStream.getTracks().forEach(track => track.stop());
      setPermissionsStatus(prev => ({
        ...prev,
        camera: { granted: true, checking: false, error: null }
      }));
    } catch (err) {
      setPermissionsStatus(prev => ({
        ...prev,
        camera: { granted: false, checking: false, error: err.message || "Camera permission denied" }
      }));
    }

    // Check microphone permission
    setPermissionsStatus(prev => ({
      ...prev,
      microphone: { ...prev.microphone, checking: true }
    }));
    
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed to check permission
      micStream.getTracks().forEach(track => track.stop());
      setPermissionsStatus(prev => ({
        ...prev,
        microphone: { granted: true, checking: false, error: null }
      }));
    } catch (err) {
      setPermissionsStatus(prev => ({
        ...prev,
        microphone: { granted: false, checking: false, error: err.message || "Microphone permission denied" }
      }));
    }

    // Check screen capture permission (this will prompt user)
    setPermissionsStatus(prev => ({
      ...prev,
      screenCapture: { ...prev.screenCapture, checking: true }
    }));
    
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: "screen",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      // Store the stream to reuse it when recording starts (don't stop it!)
      screenCaptureStreamRef.current = screenStream;
      
      // Handle user stopping screen share
      screenStream.getVideoTracks()[0].onended = () => {
        screenCaptureStreamRef.current = null;
        setPermissionsStatus(prev => ({
          ...prev,
          screenCapture: { granted: false, checking: false, error: "Screen share was stopped" }
        }));
      };
      
      setPermissionsStatus(prev => ({
        ...prev,
        screenCapture: { granted: true, checking: false, error: null }
      }));
    } catch (err) {
      setPermissionsStatus(prev => ({
        ...prev,
        screenCapture: { granted: false, checking: false, error: err.message || "Screen capture permission denied" }
      }));
    }

    setLoading(false);
  };

  // Initialize game (called after permissions are granted)
  const initializeGame = async () => {
    try {
      setLoading(true);
      setError(null);

      // Launch the web URL. Partner auth only runs when the developer
      // entered accessToken or accessCredentials for this game.
      const result = await launchGame(assignment);
      setGameUrl(result.gameUrl);

      // Game is launched, stop loading
      setLoading(false);

      // Start recording now that game is ready
      setTimeout(() => {
        requestPermissionsAndStartRecording();
      }, 1000);

    } catch (err) {
      const errorMsg = err.message || "Failed to launch game";
      setError(errorMsg);
      setLoading(false);
      if (onError) {
        onError(errorMsg);
      }
    }
  };

  // Handle Continue button click
  const handleContinue = () => {
    // Verify all permissions are granted
    const allGranted = 
      permissionsStatus.camera.granted &&
      permissionsStatus.microphone.granted &&
      permissionsStatus.screenCapture.granted;

    if (allGranted) {
      setGameReady(true);
    } else {
      // Re-check permissions if not all granted
      setPermissionsChecked(false);
      checkAllPermissions();
    }
  };

  // Retry permission check
  const retryPermissions = () => {
    // Clean up existing screen capture stream if any
    if (screenCaptureStreamRef.current) {
      screenCaptureStreamRef.current.getTracks().forEach(track => track.stop());
      screenCaptureStreamRef.current = null;
    }
    setPermissionsChecked(false);
    checkAllPermissions();
  };

  // Request permissions and start recording (non-blocking)
  const requestPermissionsAndStartRecording = async () => {
    try {
      setRecordingStatus("starting");

      // Request camera/microphone permissions (non-blocking)
      // Screen recording permission will be requested by ScreenCaptureRecorder
      try {
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (permError) {
        console.warn("Camera/microphone permission denied:", permError);
        // Continue anyway - recording manager will handle partial permissions
      }

      // Start recording manager
      // ScreenCaptureRecorder will handle screen recording permission request
      if (recordingManagerRef.current) {
        await recordingManagerRef.current.start();
        setIsRecording(true);
        setRecordingStatus("recording");
        
        // Start playtime timer
        const now = Date.now();
        setStartTime(now);
        setPlayTime(0);
        
        // Clear any existing intervals first to prevent duplicates
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        if (chimeIntervalRef.current) {
          clearInterval(chimeIntervalRef.current);
          chimeIntervalRef.current = null;
        }
        
        // Start timer interval
        timerIntervalRef.current = setInterval(() => {
          setPlayTime((prev) => prev + 1);
        }, 1000);

        // Start 10-minute chime interval
        chimeIntervalRef.current = setInterval(() => {
          playChime();
        }, 10 * 60 * 1000); // 10 minutes in milliseconds
      }
    } catch (err) {
      console.error("Error starting recording:", err);
      setRecordingStatus("error");
      // Don't set error state here - let the game continue
      // setError(`Failed to start recording: ${err.message}`);
    }
  };

  // Play chime sound (every 10 minutes)
  const playChime = () => {
    try {
      // Create audio context for chime
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Set chime frequency (pleasant tone)
      oscillator.frequency.value = 800;
      oscillator.type = "sine";

      // Set volume
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      // Play chime
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (err) {
      console.warn("Could not play chime sound:", err);
    }
  };

  // Start recording (called manually or after permissions granted)
  const startRecording = async () => {
    // Prevent duplicate starts
    if (isRecording || recordingStatus === "starting" || recordingStatus === "recording") {
      console.warn("Recording already in progress, status:", recordingStatus);
      return;
    }
    await requestPermissionsAndStartRecording();
  };

  // Stop recording
  const stopRecording = async () => {
    if (!isRecording) return;

    try {
      setRecordingStatus("stopping");

      // Clear intervals
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (chimeIntervalRef.current) {
        clearInterval(chimeIntervalRef.current);
        chimeIntervalRef.current = null;
      }

      if (recordingManagerRef.current) {
        await recordingManagerRef.current.stop();
      }

      setIsRecording(false);
      setRecordingStatus("stopped");
      setStartTime(null);
    } catch (err) {
      console.error("Error stopping recording:", err);
      setError(`Failed to stop recording: ${err.message}`);
    }
  };

  // Format playtime as MM:SS
  const formatPlayTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Cleanup intervals and streams on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (chimeIntervalRef.current) {
        clearInterval(chimeIntervalRef.current);
      }
      // Clean up screen capture stream if still active
      if (screenCaptureStreamRef.current) {
        screenCaptureStreamRef.current.getTracks().forEach(track => track.stop());
        screenCaptureStreamRef.current = null;
      }
      // Stop recording
      stopRecording();
    };
  }, []);

  // Handle close - stop recording first and wait for upload
  const handleClose = async () => {
    // Stop recording and wait for uploads to complete
    if (isRecording && recordingManagerRef.current) {
      try {
        setRecordingStatus("stopping");
        // Stop recording (this triggers uploads)
        await stopRecording();
        // Give uploads time to complete (they happen asynchronously in callbacks)
        // Wait a bit longer to ensure uploads have time to process
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (err) {
        console.error("Error stopping recording on close:", err);
        // Continue closing even if there's an error - uploads will still happen
      }
    }
    if (onClose) {
      onClose();
    }
  };

  // Handle iframe load
  const handleIframeLoad = () => {
    // Recording is already started in initializeGame(), no need to start it again here
    console.log("Game iframe loaded, recording should already be starting");
  };

  if (!assignment) {
    return (
      <div className="game-launcher">
        <div className="error">No assignment provided</div>
      </div>
    );
  }

  return (
    <div className="game-launcher">
      {/* Recording Manager */}
      <RecordingManager
        ref={recordingManagerRef}
        assignmentId={assignment.assignmentId || assignment._id}
        gameId={assignment.gameId}
        username={assignment.username}
        getScreenCaptureStream={() => screenCaptureStreamRef.current}
        onRecordingStarted={(timestamp) => {
          console.log("Recording started:", timestamp);
          setIsRecording(true);
          setRecordingStatus("recording");
        }}
        onRecordingStopped={(earnings) => {
          console.log("Recording stopped", earnings);
          setIsRecording(false);
          setRecordingStatus("stopped");
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          if (chimeIntervalRef.current) {
            clearInterval(chimeIntervalRef.current);
            chimeIntervalRef.current = null;
          }
        }}
        onError={(errorMsg) => {
          setError(errorMsg);
          setRecordingStatus("error");
        }}
      />

      {/* Permission Check Screen */}
      {permissionsChecked && !gameReady && (
        <div className="permission-check-screen">
          <div className="permission-card">
            <div className="permission-header">
              <h2>Permissions Required</h2>
              <p className="permission-subtitle">
                We need the following permissions to record your gameplay session
              </p>
            </div>

            <div className="permission-list">
              {/* Camera Permission */}
              <div className={`permission-item ${permissionsStatus.camera.granted ? 'granted' : 'denied'}`}>
                <div className="permission-icon">
                  {permissionsStatus.camera.granted ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 1l22 22M23 1L1 23"/>
                      <path d="M21 21l-4-4M3 3l4 4"/>
                      <path d="M9 9l6 6"/>
                      <path d="M15 9l-6 6"/>
                    </svg>
                  )}
                </div>
                <div className="permission-info">
                  <h3>Camera</h3>
                  <p>
                    {permissionsStatus.camera.granted 
                      ? "Camera access granted" 
                      : permissionsStatus.camera.checking 
                        ? "Checking..." 
                        : "Camera permission required for face recording"}
                  </p>
                </div>
                <div className="permission-status">
                  {permissionsStatus.camera.checking ? (
                    <div className="spinner-small"></div>
                  ) : permissionsStatus.camera.granted ? (
                    <span className="status-badge granted">✓</span>
                  ) : (
                    <span className="status-badge denied">✗</span>
                  )}
                </div>
              </div>

              {/* Microphone Permission */}
              <div className={`permission-item ${permissionsStatus.microphone.granted ? 'granted' : 'denied'}`}>
                <div className="permission-icon">
                  {permissionsStatus.microphone.granted ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="23"/>
                      <line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                  )}
                </div>
                <div className="permission-info">
                  <h3>Microphone</h3>
                  <p>
                    {permissionsStatus.microphone.granted 
                      ? "Microphone access granted" 
                      : permissionsStatus.microphone.checking 
                        ? "Checking..." 
                        : "Microphone permission required for audio recording"}
                  </p>
                </div>
                <div className="permission-status">
                  {permissionsStatus.microphone.checking ? (
                    <div className="spinner-small"></div>
                  ) : permissionsStatus.microphone.granted ? (
                    <span className="status-badge granted">✓</span>
                  ) : (
                    <span className="status-badge denied">✗</span>
                  )}
                </div>
              </div>

              {/* Screen Capture Permission */}
              <div className={`permission-item ${permissionsStatus.screenCapture.granted ? 'granted' : 'denied'}`}>
                <div className="permission-icon">
                  {permissionsStatus.screenCapture.granted ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  )}
                </div>
                <div className="permission-info">
                  <h3>Screen Capture</h3>
                  <p>
                    {permissionsStatus.screenCapture.granted 
                      ? "Screen capture access granted" 
                      : permissionsStatus.screenCapture.checking 
                        ? "Checking..." 
                        : "Screen capture permission required for gameplay recording"}
                  </p>
                </div>
                <div className="permission-status">
                  {permissionsStatus.screenCapture.checking ? (
                    <div className="spinner-small"></div>
                  ) : permissionsStatus.screenCapture.granted ? (
                    <span className="status-badge granted">✓</span>
                  ) : (
                    <span className="status-badge denied">✗</span>
                  )}
                </div>
              </div>
            </div>

            <div className="permission-actions">
              {permissionsStatus.camera.granted && 
               permissionsStatus.microphone.granted && 
               permissionsStatus.screenCapture.granted ? (
                <button 
                  className="btn btn-primary btn-continue" 
                  onClick={handleContinue}
                  disabled={loading}
                >
                  Continue to Game
                </button>
              ) : (
                <>
                  <button 
                    className="btn btn-secondary" 
                    onClick={retryPermissions}
                    disabled={loading}
                  >
                    Retry Permissions
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>

            {(!permissionsStatus.camera.granted || 
              !permissionsStatus.microphone.granted || 
              !permissionsStatus.screenCapture.granted) && (
              <div className="permission-help">
                <p>
                  <strong>Note:</strong> All permissions are required to record your gameplay session. 
                  Please grant all permissions and click "Retry Permissions" to continue.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && gameReady && (
        <div className="launcher-loading">
          <div className="loading-spinner"></div>
          <p>Launching game...</p>
          <p className="loading-subtitle">
            {assignmentRequiresAuth(assignment)
              ? "Authenticating and preparing recording"
              : "Preparing game and recording"}
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="launcher-error">
          <div className="error-icon">⚠️</div>
          <h3>Error Launching Game</h3>
          <p>{error}</p>
          {error.includes("not configured") ? (
            <div className="error-actions">
              <p className="error-hint">
                This game's authentication is not configured. Please contact support.
              </p>
              <button className="btn btn-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          ) : error.includes("Unauthorized") ? (
            <div className="error-actions">
              <p className="error-hint">
                Your session may have expired. Please log in again.
              </p>
              <button className="btn btn-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          ) : (
            <div className="error-actions">
              <button className="btn btn-secondary" onClick={() => window.location.reload()}>
                Retry
              </button>
              <button className="btn btn-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          )}
        </div>
      )}

      {/* Game Iframe */}
      {gameUrl && !error && (
        <div className="game-container">
          {/* Recording Status Indicator */}
          <div className={`recording-indicator ${isRecording ? "recording" : ""}`}>
            <span className="recording-dot"></span>
            <span className="recording-text">
              {isRecording ? "Recording" : recordingStatus === "starting" ? "Starting..." : "Not Recording"}
            </span>
          </div>

          {/* Game Iframe */}
          <iframe
            ref={iframeRef}
            src={gameUrl}
            title={assignment.gameName || "Game"}
            className="game-iframe"
            allow="camera; microphone; display-capture"
            allowFullScreen
            onLoad={handleIframeLoad}
          />

          {/* Controls */}
          <div className="game-controls">
            <div className="playtime-timer">
              <span className="timer-icon">⏱️</span>
              <span className="timer-text">{formatPlayTime(playTime)}</span>
            </div>
            <div className="game-controls-buttons">
              {typeof onRequestExternal === "function" && (
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={onRequestExternal}
                  title="Use this if the game refuses to load inside Immersely"
                >
                  Game won&apos;t load? Open in new window
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleClose}>
                Close Game
              </button>
              {isRecording && (
                <button className="btn btn-danger" onClick={stopRecording}>
                  Stop Recording
                </button>
              )}
              {!isRecording && recordingStatus === "stopped" && (
                <button className="btn btn-primary" onClick={startRecording}>
                  Start Recording Again
                </button>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

export default GameLauncher;

